import {
  EditorStore, EngineAuth, GenericPopup, IExternalCallbacks, initEditor, InitEditorOptions,
} from '@dorian/creation-tools-ui';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import shortUUID from 'short-uuid';
import { ChoiceMemoryContext } from 'contexts/ChoiceMemory/ChoiceMemoryContext';
import { logger } from 'services/loggerService/loggerService';
import { useOptionalCursor } from '../../../contexts/Cursor/CursorContext';
import { useMemoryIconsContext } from '../../../contexts/MemoryIconsContext/MemoryIconsContext';
import { useApiService } from '../../../contexts/ApiServiceContext/ApiServiceContext';
import { useAchievementsContext } from '../../../contexts/AchievementsContext/AchievementsContext';
import { useAsyncOperationState } from '../../../dorian-shared/hooks/useAsyncOperationState';
import { Character } from '../../../dorian-shared/types/character/Character';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import {
  DefaultCharacterVisualProperties,
} from '../../../dorian-shared/types/character/DefaultCharacterVisualProperties';
import { noop } from '../../../helpers/noop';
import { getRuntimeEnv } from '../../../helpers/runtimeEnv';
import { RuntimeEnv } from '../../../helpers/RuntimeEnvEnum';
import { baseURL } from '../../api';
import { VideoPlayer } from '../../pages/StoryBranches/BranchNodeCards/VideoPlayer';
import { showToast } from '../utils';
import { getBaseUrl, getEnvPrefix } from './getManifestUrlByRuntimeEnv';
import { createNodeGetter } from './getNode';
import classNames from './Preview.module.scss';
import { useAvatarResolver } from './useAvatarResolver/useAvatarResolver';
import { useChoiceMemoryCallbacks } from './useChoiceMemoryCallbacks/useChoiceMemoryCallbacks';
import { useSizes } from './useSizes';
import { useRenderStateSubscriber } from './useRenderStateSubscriber';
import { useStepAudioSubscriber } from './useStepAudioSubscriber';
import { useEmulatorActionHandler } from './useEmulatorActionHandler';
import { useEmulatorFrameData } from './useEmulatorFrameData';
import type { ResolveForwardStepDeps } from './resolveForwardStep';
import { useStepAtCursor } from './useStepAtCursor';
import { createRenderStateDeliverer } from './deliverRenderState';
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../../helpers/featureFlags';
import type { AnswerTypeItem, ModifierTypeItem } from './transformNodeToBranchDetails';
import { BookLocation } from '../../../dorian-shared/types/bookLocation/bookLocation';

const letterboxViewportSettings = {
  viewportSize: {
    width: 1080,
    height: 1920,
  },
  viewportBgColor: 0x000000,
};

type GameplayRendererOptions = InitEditorOptions['gameplayRendererOptions'];

type CorrectedInitEditorOptions = Omit<InitEditorOptions, 'episodeUuid' | 'container' | 'engineUrl' | 'gameplayRendererOptions'> & {
  gameplayRendererOptions: Omit<GameplayRendererOptions, 'localAssetsPath' | 'avatarResolver'>
};

// TODO: fix CT, force to export enum
// eslint-disable-next-line no-shadow
export declare enum GameUIStyle {
  Default = 'default',
  Arcana = 'arcana'
}

interface PreviewProps {
  bookId?: number,
  episodeUuid: string,
  store: EditorStore,
  bookStyle?: GameUIStyle,
  isDetached?: boolean,
  scrollToBranch?: (branchId: number) => void,

  width?: number,
  onPreviewExit?: (returnCode: string, params?: {episodeUuid: string}) => void
  isBranchDetailsCacheDisabled?: boolean,
  editorProps?: Partial<CorrectedInitEditorOptions>,
  characters?: Character[],
  branches?: Branch[],
  answerTypes?: AnswerTypeItem[],
  modifierTypes?: ModifierTypeItem[],
  // When provided (including null while loading), CT skips its own locations GET
  // once the array arrives. Omit on surfaces without BookLocationsContext (PlayPreview).
  bookLocations?: BookLocation[] | null,
  // needed to construct NavHistoryObject for tech-node forward resolution
  episodeId?: number,
}

const getAuth = () => {
  if (localStorage.getItem('user') && localStorage.getItem('token')) {
    return undefined;
  }

  if (!localStorage.getItem('deviceId') || localStorage.getItem('deviceId') === 'null') {
    localStorage.setItem('deviceId', shortUUID.uuid());
  }

  return { // probably not need if engine token in local storage
    type: 'deviceId',
    deviceId: localStorage.getItem('deviceId'),
  };
};

export function Preview(props: PreviewProps) {
  const {
    episodeUuid,
    store,
    bookStyle,
    isDetached = false,
    scrollToBranch,
    characters,

    isBranchDetailsCacheDisabled,
    width = 450,
    onPreviewExit = noop,
    editorProps,
    bookId,
    branches,
    answerTypes,
    modifierTypes,
    bookLocations,
    episodeId,
  } = props;

  const [storyCharacters, setStoryCharacters] = useState<Character[]>(characters ?? []);
  const { memoryIcons } = useMemoryIconsContext();
  // PlayerHas check prompts name the rewards; achievements come from the already
  // mounted AchievementsContextProvider (no extra fetch).
  const { achievements } = useAchievementsContext();

  const cursorCtx = useOptionalCursor();
  const cursor = cursorCtx?.cursor ?? null;
  const draft = cursorCtx?.draft ?? null;

  const nodeGetter = useMemo(
    () => createNodeGetter({ draft, branches: branches ?? [] }),
    [draft, branches],
  );

  const { converterCtx, fetchEtalon } = useEmulatorFrameData({
    episodeUuid,
    branches,
    storyCharacters,
    answerTypes,
    modifierTypes,
  });

  // Deliver frames to the CT store only when the controlled channel is active.
  // When the flag is OFF the deliverer is null and the emulator runs autonomously.
  const deliver = useMemo(
    () => (FEATURE_NODE_EDIT_SIDE_PANEL ? createRenderStateDeliverer(store) : null),
    // store identity is stable for the lifetime of the Preview mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store],
  );

  useRenderStateSubscriber(cursor, nodeGetter, converterCtx, fetchEtalon, deliver);
  useStepAudioSubscriber(cursor, nodeGetter, converterCtx.audios ?? [], converterCtx.audioPath ?? null);

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL || !store) return undefined;
    // Controlled keeps the emulator PREV/NEXT arrows always visible — both gestures stay
    // live while the edit form is open (NEXT = canvas-tap fn, BACK = goBack chain).
    store.setNavigationHidden(false);
    return () => { store.setNavigationHidden(false); };
  }, [store]);

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL || !store) return undefined;
    store.setControlledHistoryHasBack(
      (cursorCtx?.historyBack.length ?? 0) > 0,
    );
    // Reset on unmount: autonomous mode ignores this field, but it should not remain
    // stale if Preview is remounted in a non-controlled context.
    return () => { store.setControlledHistoryHasBack(false); };
  }, [store, cursorCtx?.historyBack.length]);

  const { mobileBackgroundStyle, sizeConfig } = useSizes(width, isDetached);
  const avatarResolver = useAvatarResolver();
  const apiService = useApiService();

  const containerRef = useRef(null);

  const choiceMemory = useContext(ChoiceMemoryContext);
  const [,
    {
      isLoading,
      setToLoading,
      setToSuccess,
      setToError,
    },
  ] = useAsyncOperationState();

  const loadData = useCallback(async () => {
    if (!bookId) {
      return;
    }
    setToLoading();
    try {
      if (!characters) {
        const charactersData = await apiService.fetchCharactersByBookId(bookId) ?? [];
        setStoryCharacters(charactersData);
        setToSuccess();
      }
    } catch (e) {
      setToError();
      showToast({ textMessage: 'Can\'t load characters', variant: 'danger' });
    }
  }, [apiService, bookId, characters, setToError, setToLoading, setToSuccess]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (choiceMemory && bookId) {
      if (!choiceMemory.currentVariables) {
        choiceMemory.fetchVariables(bookId);
      }
    }
  }, [bookId, choiceMemory]);

  // Holds the close handler of the technical-step popup currently shown in the
  // emulator (Remember/Random alert or PlayerHas prompt). Lets the cursor-change
  // effect dismiss a lingering popup and unblock its pending promise. Null = none.
  const pendingPopupDismissRef = useRef<(() => void) | null>(null);

  const popupComponent = useCallback(async (
    text: string,
    primaryButtonText?: string,
    iconId?: number,
  ) => {
    if (!store) {
      return;
    }

    await new Promise<void>((resolve) => {
      const close = () => {
        pendingPopupDismissRef.current = null;
        store.setEpisodeEditorPopupComponent(() => null);
        resolve();
      };
      // Registered so leaving the step auto-closes the alert and resolves its promise.
      pendingPopupDismissRef.current = close;
      // // TODO: Change GenericPopup to internal component
      store.setEpisodeEditorPopupComponent(() => {
        const iconUrl = memoryIcons.find((icon) => Number(icon.id) === Number(iconId))?.url;
        return (
          <div style={{ color: 'white' }}>
            {/* Will be ignore because we have different react version on CT !== engine */}
            {/* @ts-ignore */}
            <GenericPopup
              text={text}
              primaryButtonText={primaryButtonText ?? 'OK'}
              onPrimaryButtonClick={close}
              iconData={iconUrl}
            />
          </div>
        );
      });
    });
  }, [store, memoryIcons]);

  // Yes/No prompt rendered inside the emulator surface (same store slot as the
  // single-button popup). PlayerHas checks have no memory state to evaluate, so
  // the author answers whether the player owns the reward; Yes -> true branch.
  const confirmPlayerHas = useCallback(async (text: string): Promise<boolean> => {
    if (!store) {
      return false;
    }
    return new Promise<boolean>((resolve) => {
      const settle = (value: boolean) => {
        pendingPopupDismissRef.current = null;
        store.setEpisodeEditorPopupComponent(() => null);
        resolve(value);
      };
      // Dismiss-by-navigation resolves false; the action handler's cursor guard then
      // drops the stale routing, so the false branch is never taken on dismiss.
      pendingPopupDismissRef.current = () => settle(false);
      store.setEpisodeEditorPopupComponent(() => (
        <div style={{ color: 'white' }}>
          {/* @ts-ignore CT runs a different React version than the engine */}
          <GenericPopup
            text={text}
            primaryButtonText="Yes"
            secondaryButtonText="No"
            onPrimaryButtonClick={() => settle(true)}
            onSecondaryButtonClick={() => settle(false)}
          />
        </div>
      ));
    });
  }, [store]);

  // Single entry point to close whatever technical-step popup is open.
  const dismissActivePopup = useCallback(() => {
    const dismiss = pendingPopupDismissRef.current;
    pendingPopupDismissRef.current = null;
    dismiss?.();
  }, []);

  // Auto-dismiss when the author leaves the step the popup belongs to. The cursor
  // is stable while the popup is shown, so this only fires on a real move (clicking
  // another node, back/forward) — never on the popup's own opening frame.
  useEffect(() => {
    dismissActivePopup();
  }, [cursor?.branchId, cursor?.stepIndex, dismissActivePopup]);

  // Never leave a popup (and its pending promise) hanging if the emulator unmounts.
  useEffect(() => () => dismissActivePopup(), [dismissActivePopup]);

  const actionDeps = useMemo<ResolveForwardStepDeps | null>(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL || episodeId === undefined) return null;
    return {
      choiceMemory: choiceMemory ?? undefined, popupComponent, episodeId, confirmPlayerHas, achievements,
    };
  }, [choiceMemory, episodeId, popupComponent, confirmPlayerHas, achievements]);

  useEmulatorActionHandler(store, cursorCtx, nodeGetter, actionDeps);

  const { handleExternalCallbacks, branchStep } = useChoiceMemoryCallbacks(
    choiceMemory,
    popupComponent,
    scrollToBranch,
    storyCharacters,
    FEATURE_NODE_EDIT_SIDE_PANEL && cursorCtx !== null,
    confirmPlayerHas,
    achievements,
  );

  const cursorStep = useStepAtCursor(cursor, nodeGetter);
  const effectiveBranchStep = cursorStep ?? branchStep;

  const externalCallbacksRef = useRef(handleExternalCallbacks);
  externalCallbacksRef.current = handleExternalCallbacks;

  const variableMapRef = useRef(converterCtx.maps?.variableMap ?? {});
  variableMapRef.current = converterCtx.maps?.variableMap ?? {};

  const stableExternalCallbacks = useMemo<IExternalCallbacks>(() => ({
    resolveVariableValue: (key: string) => externalCallbacksRef.current.resolveVariableValue?.(key),
    onBeforeTextRender: (props) => externalCallbacksRef.current.onBeforeTextRender?.(props) ?? props.text,
    onNavHistoryStackChanged: (props) => externalCallbacksRef.current.onNavHistoryStackChanged?.(props) ?? Promise.resolve(undefined),
    resolveVariableName: (variableId: number) => variableMapRef.current[variableId],
  }), []);

  useEffect(() => {
    if (!store) {
      logger.debug('store is null. Returning...');
      return () => null;
    }

    if (!containerRef.current) {
      logger.debug('containerRef.current is null. Returning...');
      return () => null;
    }
    if (!avatarResolver) {
      logger.debug('avatarResolver is null. Returning...');
      return () => null;
    }

    const waitForProvidedLocations = bookLocations !== undefined && bookLocations === null;
    if (waitForProvidedLocations) {
      logger.debug('bookLocations not loaded yet. Returning...');
      return () => null;
    }

    // Legacy. Just set any string, because we use credentials  instead bearer token
    localStorage.setItem('engineToken', 'credential');

    const runtimeEnv = getRuntimeEnv();
    const envLabel = runtimeEnv === RuntimeEnv.Prod ? 'prod' : 'staging';
    const assetsEnvironment = getEnvPrefix(envLabel);
    const assetsUrl = getBaseUrl(envLabel);

    const initOptions = {
      letterboxViewportSettings,
      currentUserCharacterProps: {} as DefaultCharacterVisualProperties,
      exitCallback: onPreviewExit,
      logAnalyticsEvent: noop,
      assetsUrl,
      assetsEnvironment,
      ...editorProps,
      gameplayRendererOptions: {
        mouseEventsEnabled: true,
        ...sizeConfig,
        ...editorProps?.gameplayRendererOptions,
        localAssetsPath: `${process.env.PUBLIC_URL}/`,
        avatarResolver,
        viewPortSize: sizeConfig,
      },
      auth: getAuth() as EngineAuth,
      episodeUuid,
      container: containerRef.current,
      engineUrl: baseURL.slice(0, -1),
      externalStore: store,
      isReadOnly: true,
      isBranchDetailsCacheDisabled,
      isLetterboxForArcanaActive: true,
      isLetterboxForAllStoriesActive: false,
      externalCallbacks: stableExternalCallbacks,
      bookLocations: bookLocations ?? undefined,
    };
    const unmountEpisodeEditor = initEditor(initOptions as InitEditorOptions);

    return () => {
      unmountEpisodeEditor?.();
      localStorage.removeItem('engineToken');
    };
  }, [
    avatarResolver,
    editorProps,
    episodeUuid,
    onPreviewExit,
    sizeConfig,
    store,
    isBranchDetailsCacheDisabled,
    stableExternalCallbacks,
    // Re-run when locations go from null (waiting) to loaded. Do not remount when
    // the catalog later refreshes — that update is applied in-place on the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    bookLocations == null,
  ]);

  return (
    <div
      className={isDetached ? undefined : classNames.wrapper}
      style={mobileBackgroundStyle}
    >
      <div id="errorsBox" />
      <div
        ref={containerRef}
        className={isDetached ? classNames.previewContainerDetached : classNames.previewContainer}
        style={sizeConfig}
      />
      {isLoading
        && (
          <>
            <div className={classNames.overlay} />
            <div className={classNames.spinner}>
              <Spinner variant="primary" animation="border" />
            </div>
          </>
        )}
      {
        effectiveBranchStep?.video && !isLoading && (
          <div
            className={isDetached ? classNames.videoPlayerContainerDetached : classNames.videoPlayerContainer}
            style={sizeConfig}
          >
            <div className={classNames.videoPlayerVideo}>
              <VideoPlayer
                videoUrl={effectiveBranchStep.video.url}
                videoTitle={effectiveBranchStep.video.title}
                customControls={!isDetached}
                isPlaying
                muted
              />
            </div>
            <div className={classNames.videoPlayerTextContainer}>
              <div className={classNames.videoPlayerAnnouncement}>
                <div>
                  <span>This is a mockup</span>
                </div>
                The actual video will be positioned and sized differently.
              </div>
              <div className={classNames.videoPlayerTextTitle}>
                <span>Video Title:</span>
                <br />
                {effectiveBranchStep.video.title}
                <br />
                <br />
                <span>Allow Step Action:</span>
                {effectiveBranchStep.video.videoSettings?.allowStepActions ? ' Yes' : ' No'}
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
}
